import { useContext, useState } from 'react';

import { VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useFilterItemsStore from '@/stores/FilterItemsStore';
import { ReanalyzeStatus } from '@/types/filterItems';

import FilterItemText from './FilterItemText';
import { FilterTextContext } from './provider/FilterTextsProvider';
import { DiffType } from './type';

enum ErrorText {
  ErrTooLong = 'Text should be less than 1000 characters',
  ErrDuplicated = 'Same text already exists',
}

const MAX_TEXT_LENGTH = 1000;

const FilterItemsAddText = () => {
  const color = useColor();
  const [newTextFilter, setNewTextFilter] = useState<string>('');
  const [error, setError] = useState<ErrorText | null>(null);
  const { reanalyzeStatus } = useFilterItemsStore();
  const { setUpdatedTexts, existingTexts, updatedTexts } =
    useContext(FilterTextContext);

  const textsToCompare = existingTexts.concat(
    updatedTexts.map((text) => text.text),
  );

  const validateText = (text: string) => {
    if (text.length > MAX_TEXT_LENGTH) {
      return ErrorText.ErrTooLong;
    }

    if (textsToCompare.includes(text)) {
      return ErrorText.ErrDuplicated;
    }

    return undefined;
  };

  const handleChangeNewText = (value: string) => {
    const error = validateText(value);

    setNewTextFilter(value);

    if (error) {
      setError(error);
    } else {
      setError(null);
    }
  };

  const initializeTextFilter = () => {
    setNewTextFilter('');
    setError(null);
  };

  const handleConfirmAddText = () => {
    if (confirmDisabled) {
      return;
    }

    if (!newTextFilter) {
      return;
    }

    setUpdatedTexts((prev) => [
      ...prev,
      { type: DiffType.ADDED, text: newTextFilter },
    ]);

    initializeTextFilter();
  };

  const confirmDisabled = Boolean(!newTextFilter || error);

  const editDisabled =
    reanalyzeStatus === ReanalyzeStatus.REANALYZE_STATUS_RUNNING;

  return (
    <VFlex g={6}>
      <FilterItemText
        initFocus
        readOnly={editDisabled}
        text={newTextFilter}
        onChange={handleChangeNewText}
        actionButton={
          <Button
            variant="ghost"
            sizeType="m"
            onClick={handleConfirmAddText}
            disabled={confirmDisabled || editDisabled}
          >
            Add
          </Button>
        }
        placeholder="type phrases or sentences to filter out from the analysis"
      />
      {error && (
        <Typo weight="m" color={color.red600}>
          {error}
        </Typo>
      )}
    </VFlex>
  );
};

export default FilterItemsAddText;
